// Client side of archive playback and export (API: GET /recordings/{id}/media, POST
// /recordings/{id}/export; docs/implementation/OPUS-API-INTEGRATION.md section 4). These two routes
// are not in packages/contracts yet, so the narrow shapes they need live here.
//
// The server is the authority on every state. Audio plays straight from the media route through the
// browser's same-origin cookie session; nothing is buffered, cached or re-hosted here. When the
// audio element fails, one small ranged GET reads the server's error code so the exact state can be
// shown; if the media itself was fine, the source is requested once more, and a second failure is
// reported rather than retried again.
import type { Recording } from '@bcio-voice/contracts';
import { type ApiClient, isAbortError, isApiRequestError } from '../../lib/api.ts';
import { formatDuration } from '../../lib/format.ts';

export const mediaPath = (recordingId: string) => `/recordings/${encodeURIComponent(recordingId)}/media`;
export const exportPath = (recordingId: string) => `/recordings/${encodeURIComponent(recordingId)}/export`;

export type MergedRecordingContinuity =
  | 'single_segment_exact'
  | 'timestamp_anchored_segments'
  | 'overlap_trimmed'
  | 'with_gaps';

export interface MergedRecordingSegmentProvenance {
  id: string;
  index: number;
  start_ms: number;
  end_ms: number;
  duration_ms: number;
  start_sample: number;
  end_sample: number;
  trimmed_overlap_ms: number;
  policy_revision: number;
  status: string;
}

export interface MergedRecordingGapProvenance {
  after_segment_id: string;
  before_segment_id: string;
  position_ms: number;
  duration_ms: number;
  start_sample: number;
  end_sample: number;
  kind: 'paused_or_excluded' | 'segment_rollover';
  reason: string;
}

export interface RecordingWithProvenance extends Recording {
  merged?: boolean;
  segment_count?: number;
  overlap_trimmed_ms?: number;
  continuity?: MergedRecordingContinuity;
  continuity_note?: string;
  segments?: MergedRecordingSegmentProvenance[];
  gaps?: MergedRecordingGapProvenance[];
}

export function describeRecordingGap(gap: MergedRecordingGapProvenance): string {
  const at = formatDuration(gap.position_ms);
  const length = formatDuration(Math.max(1000, gap.duration_ms));
  return gap.kind === 'paused_or_excluded'
    ? `Paused or consent-excluded span (${length}) at ${at} — not recorded.`
    : `Unrecorded segment handover gap (${length}) at ${at}.`;
}

/** Coalesces multiple raw segment items for the same call into one logical merged recording if not already merged by the API. */
export function coalesceCallRecordings(items: readonly RecordingWithProvenance[]): RecordingWithProvenance[] {
  if (items.length <= 1) return [...items];
  const first = items[0]!;
  if (items.some(r => r.call_id !== first.call_id)) return [...items];
  const ordered = [...items].sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at) || a.id.localeCompare(b.id));
  let cursorMs = 0;
  const segments: MergedRecordingSegmentProvenance[] = ordered.map((r, i) => {
    const startMs = cursorMs;
    const endMs = startMs + Math.max(0, r.duration_ms);
    cursorMs = endMs;
    return {
      id: r.id,
      index: i + 1,
      start_ms: startMs,
      end_ms: endMs,
      duration_ms: Math.max(0, r.duration_ms),
      start_sample: startMs * 16,
      end_sample: endMs * 16,
      trimmed_overlap_ms: 0,
      policy_revision: 1,
      status: r.status,
    };
  });
  const anyFailed = ordered.some(r => r.status === 'failed');
  const anyPartial = ordered.some(r => r.status === 'partial' || r.status === 'deleted' || r.status === 'deletion_requested');
  const allReady = !anyFailed && !anyPartial && ordered.every(r => (r.status === 'ready' || r.status === 'verified') && r.available);
  const allSaved = !anyFailed && !anyPartial && ordered.every(r => r.status === 'ready' || r.status === 'verified');
  const status = anyFailed ? 'failed' : anyPartial ? 'partial' : allReady || allSaved ? 'ready' : 'processing';
  const available = allReady;
  const downloadAllowed = available && ordered.every(r => r.download_allowed);
  const retentions = ordered
    .map(r => (r.retention_until ? Date.parse(r.retention_until) : null))
    .filter((t): t is number => t !== null && Number.isFinite(t));
  const earliestRetention = retentions.length ? new Date(Math.min(...retentions)).toISOString() : null;
  return [{
    id: first.id,
    call_id: first.call_id,
    status,
    duration_ms: cursorMs,
    channels: first.channels,
    created_at: ordered[0]!.created_at,
    retention_until: earliestRetention,
    available,
    download_allowed: downloadAllowed,
    merged: true,
    segment_count: ordered.length,
    overlap_trimmed_ms: 0,
    continuity: 'timestamp_anchored_segments',
    continuity_note: 'Assembled from ordered segments anchored to PBX RecordingStarted timestamps; boundary alignment is bounded by PBX event timestamp resolution.',
    segments,
    gaps: [],
  }];
}

/** Why a recording can't be played or downloaded right now. */
export type ArchiveProblem =
  | 'desktop' // 403 browser_session_required: a desktop bearer principal
  | 'denied' // 403: no playback or export access
  | 'not_found' // 404: deleted, or not visible to this person
  | 'unconfigured' // 503 archive_unconfigured
  | 'unavailable' // 503 archive_unavailable and other server failures
  | 'session' // 401: the session ended
  | 'offline' // no response at all
  | 'failed'; // the media failed again after one re-request

export interface ArchiveFailure {
  problem: ArchiveProblem;
  code: string;
  requestId: string | null;
}

export function classifyArchiveError(error: unknown): ArchiveFailure {
  if (!isApiRequestError(error)) return { problem: 'failed', code: 'client_error', requestId: null };
  const at = { code: error.code, requestId: error.requestId };
  if (error.status === 0) return { problem: 'offline', ...at };
  if (error.status === 401) return { problem: 'session', ...at };
  if (error.status === 403) return { problem: error.code === 'browser_session_required' ? 'desktop' : 'denied', ...at };
  if (error.status === 404) return { problem: 'not_found', ...at };
  if (error.code === 'archive_unconfigured') return { problem: 'unconfigured', ...at };
  if (error.status >= 500 || error.status === 416) return { problem: 'unavailable', ...at };
  return { problem: 'failed', ...at };
}

export interface ArchiveCopy {
  title: string;
  body: string;
  variant: 'unconfigured' | 'unavailable' | 'denied' | 'offline' | 'dependency' | 'error';
  /** Whether trying again could help. */
  retry: boolean;
}

export const DESKTOP_PLAYBACK: ArchiveCopy = {
  title: 'Playback needs the web session',
  body: 'Open Sidetone in your web browser to play or download recordings. The desktop app can’t stream them.',
  variant: 'unavailable',
  retry: false,
};

export const PLAYBACK_NOT_SET_UP: ArchiveCopy = {
  title: 'Playback isn’t set up yet',
  body: 'This workspace can’t play recordings until an administrator connects the recording archive.',
  variant: 'unconfigured',
  retry: false,
};

export function archiveCopy(problem: ArchiveProblem): ArchiveCopy {
  switch (problem) {
    case 'desktop':
      return DESKTOP_PLAYBACK;
    case 'unconfigured':
      return PLAYBACK_NOT_SET_UP;
    case 'denied':
      return { title: 'You don’t have playback access', body: 'This recording exists, but your access doesn’t include playing or downloading it. Ask an admin if you need it.', variant: 'denied', retry: false };
    case 'not_found':
      return { title: 'This recording isn’t available to you', body: 'It may have been deleted, or it isn’t shared with you.', variant: 'denied', retry: false };
    case 'session':
      return { title: 'Sign in again to play recordings', body: 'Your session ended.', variant: 'denied', retry: false };
    case 'offline':
      return { title: 'You’re offline', body: 'Recordings play when you’re back online.', variant: 'offline', retry: true };
    case 'unavailable':
      return { title: 'The recording archive is unavailable right now', body: 'The recording isn’t affected. Try again in a few minutes.', variant: 'dependency', retry: true };
    default:
      return { title: 'This recording couldn’t be played', body: 'It was requested again once and still didn’t play. Try again later.', variant: 'error', retry: true };
  }
}

type Opener = Pick<ApiClient, 'open'>;

/** One-byte ranged read of the media route: null when the server would serve the audio, else why not. */
export async function probeMedia(api: Opener, recordingId: string, signal?: AbortSignal): Promise<ArchiveFailure | null> {
  try {
    const response = await api.open(mediaPath(recordingId), { headers: { Range: 'bytes=0-0' }, ...(signal ? { signal } : {}) });
    await response.body?.cancel().catch(() => undefined);
    return null;
  } catch (error) {
    if (isAbortError(error)) throw error;
    return classifyArchiveError(error);
  }
}

/** A safe local file name from `Content-Disposition`, or null. */
export function filenameFrom(disposition: string | null): string | null {
  const match = disposition ? /filename="?([^";]+)"?/i.exec(disposition) : null;
  const name = match?.[1]?.replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 120) ?? '';
  return name && !name.startsWith('.') ? name : null;
}

/** Saves a downloaded file through a temporary object URL (revoked afterwards). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** POST /recordings/{id}/export (CSRF, audited by the server). Null on success. Never retried. */
export async function exportRecording(api: Opener, recordingId: string, save: (blob: Blob, filename: string) => void = saveBlob): Promise<ArchiveFailure | null> {
  try {
    const response = await api.open(exportPath(recordingId), { method: 'POST' });
    const blob = await response.blob();
    save(blob, filenameFrom(response.headers.get('Content-Disposition')) ?? `recording-${recordingId}.wav`);
    return null;
  } catch (error) {
    return classifyArchiveError(error);
  }
}

// ---- Player -------------------------------------------------------------------------------------

/** The parts of HTMLAudioElement the player uses; tests pass a fake. */
export interface AudioElementLike {
  src: string;
  preload: string;
  currentTime: number;
  readonly duration: number;
  playbackRate: number;
  readonly paused: boolean;
  play(): Promise<void>;
  pause(): void;
  load(): void;
  removeAttribute(name: string): void;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface PlayerSnapshot {
  /** `retrying`: the media failed and the server is being asked why (and asked again once). */
  status: 'idle' | 'loading' | 'ready' | 'retrying' | 'problem';
  playing: boolean;
  /** Seconds. */
  position: number;
  /** Seconds; null until the audio reports it and no hint was given. */
  duration: number | null;
  rate: number;
  failure: ArchiveFailure | null;
}

export const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 2] as const;
export const SKIP_SECONDS = 10;

/** Keyboard scrubbing on the position slider (UX 6.5): arrows ±5 s, Page Up/Down ±30 s, Home/End. */
export function scrubTarget(key: string, position: number, duration: number | null): number | null {
  const end = duration ?? position;
  switch (key) {
    case 'ArrowLeft':
    case 'ArrowDown':
      return Math.max(0, position - 5);
    case 'ArrowRight':
    case 'ArrowUp':
      return Math.min(end, position + 5);
    case 'PageDown':
      return Math.max(0, position - 30);
    case 'PageUp':
      return Math.min(end, position + 30);
    case 'Home':
      return 0;
    case 'End':
      return end;
    default:
      return null;
  }
}

const MEDIA_EVENTS = ['loadedmetadata', 'durationchange', 'timeupdate', 'play', 'pause', 'ended', 'error'] as const;

export interface RecordingPlaybackOptions {
  /** Absolute same-origin media URL (ApiClient.url(mediaPath(id))). */
  url: string;
  /** Asks the server why the media failed; null means it would serve the audio. */
  probe: () => Promise<ArchiveFailure | null>;
  durationHintMs?: number;
}

/**
 * Playback state for one recording on one audio element. Starts paused and requests nothing until
 * attached. On a media error it asks the server why: an error code becomes that exact state; if the
 * server would serve the audio (for example an expired link or a dropped connection), the source is
 * requested once more at the same position, and a second failure is reported.
 */
export class RecordingPlayback {
  private snapshot: PlayerSnapshot;
  private readonly listeners = new Set<() => void>();
  private el: AudioElementLike | null = null;
  private detachEl: (() => void) | null = null;
  private retried = false;
  private handling = false;
  private generation = 0;
  private resumeAt: number | null = null;
  private resumePlaying = false;
  private disposed = false;

  constructor(private readonly options: RecordingPlaybackOptions) {
    this.snapshot = { status: 'idle', playing: false, position: 0, duration: options.durationHintMs ? options.durationHintMs / 1000 : null, rate: 1, failure: null };
  }

  getSnapshot = (): PlayerSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  attach(el: AudioElementLike): () => void {
    this.detachEl?.();
    this.el = el;
    const handlers = MEDIA_EVENTS.map(type => [type, () => this.onMedia(type)] as const);
    for (const [type, fn] of handlers) el.addEventListener(type, fn);
    this.detachEl = () => {
      for (const [type, fn] of handlers) el.removeEventListener(type, fn);
      if (this.el === el) this.el = null;
      this.detachEl = null;
    };
    el.preload = 'metadata';
    el.playbackRate = this.snapshot.rate;
    this.load();
    return () => this.release(el);
  }

  toggle(): void {
    const el = this.el;
    if (!el || this.snapshot.status === 'problem' || this.snapshot.status === 'retrying') return;
    if (!el.paused) {
      el.pause();
      return;
    }
    // A refusal here (for example a source error) also fires `error`, which is handled there.
    void el.play().catch(() => undefined);
  }

  seekTo(seconds: number): void {
    const el = this.el;
    if (!el || this.snapshot.status === 'problem') return;
    const end = this.snapshot.duration ?? Number.POSITIVE_INFINITY;
    const target = Math.min(Math.max(0, seconds), end);
    el.currentTime = target;
    this.set({ position: target });
  }

  seekBy(deltaSeconds: number): void {
    this.seekTo((this.el?.currentTime ?? this.snapshot.position) + deltaSeconds);
  }

  setRate(rate: number): void {
    if (!(PLAYBACK_RATES as readonly number[]).includes(rate)) return;
    if (this.el) this.el.playbackRate = rate;
    this.set({ rate });
  }

  /** User asked to try again after a reported problem: one fresh attempt with its own re-request. */
  retry(): void {
    if (!this.el || this.disposed) return;
    this.retried = false;
    this.resumeAt = this.snapshot.position > 0 ? this.snapshot.position : null;
    this.resumePlaying = false;
    this.load();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.el) this.release(this.el);
    this.listeners.clear();
  }

  private release(el: AudioElementLike): void {
    if (this.el !== el) return;
    this.generation += 1;
    try { el.pause(); } catch { /* already stopped */ }
    this.detachEl?.();
    el.removeAttribute('src');
    try { el.load(); } catch { /* nothing loaded */ }
  }

  private load(): void {
    const el = this.el;
    if (!el) return;
    this.generation += 1;
    this.handling = false;
    this.set({ status: 'loading', playing: false, failure: null });
    el.src = this.options.url;
    el.load();
  }

  private onMedia(type: (typeof MEDIA_EVENTS)[number]): void {
    const el = this.el;
    if (!el || this.disposed) return;
    switch (type) {
      case 'loadedmetadata':
      case 'durationchange': {
        const duration = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : this.snapshot.duration;
        if (type === 'loadedmetadata' && this.resumeAt !== null) {
          el.currentTime = Math.min(this.resumeAt, duration ?? this.resumeAt);
          this.resumeAt = null;
          if (this.resumePlaying) void el.play().catch(() => undefined);
          this.resumePlaying = false;
        }
        this.set({ status: this.snapshot.status === 'loading' ? 'ready' : this.snapshot.status, duration, position: el.currentTime });
        return;
      }
      case 'timeupdate':
        this.set({ position: el.currentTime });
        return;
      case 'play':
        this.set({ playing: true, status: this.snapshot.status === 'loading' ? 'ready' : this.snapshot.status });
        return;
      case 'pause':
      case 'ended':
        this.set({ playing: false, position: el.currentTime });
        return;
      case 'error':
        void this.onError();
        return;
    }
  }

  private async onError(): Promise<void> {
    if (this.handling || this.snapshot.status === 'problem') return;
    this.handling = true;
    const generation = this.generation;
    const at = this.el?.currentTime || this.snapshot.position;
    const wasPlaying = this.snapshot.playing;
    this.set({ status: 'retrying', playing: false });
    let failure: ArchiveFailure | null;
    try {
      failure = await this.options.probe();
    } catch {
      failure = { problem: 'failed', code: 'probe_aborted', requestId: null };
    }
    if (generation !== this.generation || this.disposed) return;
    this.handling = false;
    if (!failure && this.retried) failure = { problem: 'failed', code: 'media_error', requestId: null };
    if (failure) {
      this.set({ status: 'problem', failure, playing: false });
      return;
    }
    this.retried = true;
    this.resumeAt = at > 0 ? at : null;
    this.resumePlaying = wasPlaying;
    this.load();
  }

  private set(patch: Partial<PlayerSnapshot>): void {
    const next = { ...this.snapshot, ...patch };
    if ((Object.keys(patch) as (keyof PlayerSnapshot)[]).every(k => next[k] === this.snapshot[k])) return;
    this.snapshot = next;
    for (const l of [...this.listeners]) l();
  }
}
